import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetEmailRevealHistory } from '@/react-query/podcast-queries';
import dayjs from 'dayjs';
import { Search, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { mapUsageLogFromApi } from "@/utils/mapper";

const UsageHistory = () => {
    const [page, setPage] = useState(1);
    
    // Default to show current month history
    const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
    const [appliedStartDate, setAppliedStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [appliedEndDate, setAppliedEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

    const handleSearch = () => {
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setPage(1);
    };

    // React Query expects 0-based page index
    const { data: response, isLoading } = useGetEmailRevealHistory(
        page - 1, 
        10, 
        appliedStartDate ? dayjs(appliedStartDate).startOf('day').toISOString() : undefined,
        appliedEndDate ? dayjs(appliedEndDate).endOf('day').toISOString() : undefined
    );

    const historyData = response?.data;
    const logs = (historyData?.content || []).map(mapUsageLogFromApi);
    const totalPages = historyData?.totalPages || 0;

    return (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden flex flex-col h-full"> 
            <div className="p-4 border-b bg-slate-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                 <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                         <History className="h-5 w-5 text-primary" /> Reveal History
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">History of all email reveals across podcasts</p>
                 </div>
                 
                 <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex gap-2">
                        <Input 
                            type="date" 
                            className="bg-white"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <Input 
                            type="date"
                             className="bg-white"
                             value={endDate}
                             onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <Button onClick={handleSearch} className="gap-2">
                        <Search className="h-4 w-4" /> Filter
                    </Button>
                 </div>
            </div>

            <div className="p-0">
                {/* Desktop Table View */}
                <div className="hidden md:block">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead>Description</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="h-24 text-center">Loading history...</TableCell>
                                </TableRow>
                            ) : logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="h-24 text-center">No history found for this period.</TableCell>
                                </TableRow>
                            ) : (
                                logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-3">
                                                 <Avatar className="h-10 w-10 border">
                                                    <AvatarImage src={log.imageUrl} />
                                                    <AvatarFallback>{log.description?.substring(0, 2)}</AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col max-w-[200px] md:max-w-xs">
                                                    <span className="font-medium truncate" title={log.description}>
                                                        {log.description}
                                                    </span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-gray-500">
                                             <span className={`text-xs font-medium px-2 py-1 rounded ${
                                                 log.type === 'COUPON' ? 'bg-green-100 text-green-700' :
                                                 log.creditSource === 'EXTRA_CREDIT' ? 'bg-purple-100 text-purple-700' : 
                                                 'bg-slate-100 text-slate-700'
                                             }`}>
                                                {log.type === 'COUPON' ? 'Coupon' : 
                                                 log.creditSource === 'EXTRA_CREDIT' ? 'Extra Credit' : 'Subscription'}
                                             </span>
                                        </TableCell>
                                        <TableCell className="text-gray-500">
                                            {dayjs(log.date).format('MMM D, YYYY h:mm A')}
                                        </TableCell>
                                        <TableCell className="text-right">
                                             {log.referenceId && (
                                                <Button variant="ghost" size="sm" asChild>
                                                    <Link to={`/dashboard/podcasts/${log.referenceId}`}>
                                                        View
                                                    </Link>
                                                </Button>
                                             )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Mobile Card List View */}
                <div className="md:hidden">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading history...</div>
                    ) : logs.length === 0 ? (
                         <div className="p-8 text-center text-muted-foreground">No history found for this period.</div>
                    ) : (
                        <div className="divide-y">
                            {logs.map((log) => (
                                <div key={log.id} className="p-4 flex flex-col gap-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <Avatar className="h-10 w-10 border shrink-0">
                                                <AvatarImage src={log.imageUrl} />
                                                <AvatarFallback>{log.description?.substring(0, 2)}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex flex-col min-w-0">
                                                <span className="font-medium truncate block" title={log.description}>
                                                    {log.description}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {dayjs(log.date).format('MMM D, YYYY h:mm A')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between">
                                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                                             log.type === 'COUPON' ? 'bg-green-100 text-green-700' :
                                             log.creditSource === 'EXTRA_CREDIT' ? 'bg-purple-100 text-purple-700' : 
                                             'bg-slate-100 text-slate-700'
                                         }`}>
                                            {log.type === 'COUPON' ? 'Coupon' : 
                                             log.creditSource === 'EXTRA_CREDIT' ? 'Extra Credit' : 'Subscription'}
                                         </span>
                                         
                                         {log.referenceId && (
                                            <Button variant="outline" size="sm" className="h-8" asChild>
                                                <Link to={`/dashboard/podcasts/${log.referenceId}`}>
                                                    View Podcast
                                                </Link>
                                            </Button>
                                         )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {totalPages > 1 && (
                <div className="p-4 border-t flex justify-center gap-2">
                     <Button 
                         variant="outline" 
                         disabled={page <= 1} 
                         onClick={() => setPage(page - 1)}
                     >
                        Previous
                     </Button>
                     <div className="flex items-center px-4 text-sm font-medium">
                        Page {page} of {totalPages}
                     </div>
                     <Button 
                         variant="outline" 
                         disabled={page >= totalPages} 
                         onClick={() => setPage(page + 1)}
                     >
                        Next
                     </Button>
                </div>
            )}
        </div>
    );
};

export default UsageHistory;
